import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  deleteConversation,
  listConversations,
  updateConversation,
  type ConversationSummary,
} from '../lib/chatApi';

/**
 * AI-assistant conversation history (CLE-45), shared between the page and the
 * app sidebar. The two live in different React trees — the sidebar renders in
 * `Layout`, the chat in the routed page — so the list has to be cached rather
 * than held in either one's state.
 */

/** Prefix key: invalidating it refreshes every cached search. */
const LIST_KEY = ['chat', 'conversations'];

export function useChatConversations(search: string) {
  return useQuery<ConversationSummary[]>({
    queryKey: [...LIST_KEY, search],
    queryFn: () => listConversations(search),
    // Keep the previous list on screen while a new search is in flight, so the
    // sidebar doesn't blank out between keystrokes.
    placeholderData: (previous) => previous,
  });
}

/** Refresh the list after a turn creates, titles or updates a thread. */
export function useRefreshChatConversations(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: LIST_KEY });
  }, [queryClient]);
}

export function useUpdateChatConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: { title?: string; pinned?: boolean } }) =>
      updateConversation(id, changes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useDeleteChatConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConversation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}
